import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiArrowRight, FiGlobe, FiMenu, FiPhoneCall, FiX } from "react-icons/fi";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaXTwitter } from "react-icons/fa6";

import { companyInfo, navigationLinks } from "../../data/siteContent";

const socialIcons = {
  Facebook: FaFacebookF,
  Instagram: FaInstagram,
  X: FaXTwitter,
  LinkedIn: FaLinkedinIn,
};

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const updateScrollState = () => {
      setIsScrolled(window.scrollY > 24);
    };

    updateScrollState();
    window.addEventListener("scroll", updateScrollState);

    return () => window.removeEventListener("scroll", updateScrollState);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("menu-open", menuOpen);

    return () => document.body.classList.remove("menu-open");
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  const toggleMenu = () => setMenuOpen((current) => !current);
  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header className={`site-nav ${isScrolled ? "is-scrolled" : ""}`}>
        <div className="section-shell site-nav__inner">
          <Link to="/" className="site-nav__brand" onClick={closeMenu}>
            <img src={companyInfo.logo} alt={companyInfo.name} className="site-nav__logo" />
            <div className="site-nav__brand-copy">
              <span className="site-nav__name">{companyInfo.name}</span>
            </div>
          </Link>

          <nav className="site-nav__links" aria-label="Primary">
            {navigationLinks.map((link) => {
              const isActive = link.matchPaths.includes(location.pathname);

              return (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`site-nav__link${isActive ? " is-active" : ""}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="site-nav__actions">
            <div className="site-nav__contact">
              <FiPhoneCall aria-hidden="true" />
              <div className="site-nav__contact-copy">
                <a href={companyInfo.phoneHref}>{companyInfo.primaryPhone}</a>
                <a href={companyInfo.secondaryPhoneHref}>{companyInfo.secondaryPhone}</a>
              </div>
            </div>

            <div className="site-nav__social" aria-label="Social links">
              {companyInfo.socialLinks.map((social) => {
                const Icon = socialIcons[social.label];

                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={social.label}
                  >
                    <Icon aria-hidden="true" />
                  </a>
                );
              })}
            </div>

            <Link className="button button-primary site-nav__cta" to="/contact">
              Request Quote
              <FiArrowRight aria-hidden="true" />
            </Link>

            <button
              type="button"
              className="site-nav__toggle"
              onClick={toggleMenu}
              aria-expanded={menuOpen}
              aria-controls="mobile-navigation"
              aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {menuOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      <div
        className={`site-nav__backdrop${menuOpen ? " is-open" : ""}`}
        onClick={closeMenu}
        aria-hidden={menuOpen ? "false" : "true"}
      />

      <aside
        id="mobile-navigation"
        className={`site-nav__drawer${menuOpen ? " is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="site-nav__drawer-header">
          <div>
            <p className="section-kicker">Thamizhi International Ventures</p>
          </div>

          <button
            type="button"
            className="site-nav__drawer-close"
            onClick={closeMenu}
            aria-label="Close navigation menu"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        <nav className="site-nav__drawer-links" aria-label="Mobile">
          {navigationLinks.map((link) => {
            const isActive = link.matchPaths.includes(location.pathname);

            return (
              <Link
                key={link.label}
                to={link.to}
                onClick={closeMenu}
                className={`site-nav__drawer-link${isActive ? " is-active" : ""}`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="site-nav__drawer-meta">
          <div className="site-nav__drawer-contact-stack">
            <a href={companyInfo.phoneHref} className="site-nav__drawer-contact">
              <FiPhoneCall aria-hidden="true" />
              {companyInfo.primaryPhone}
            </a>
            <a href={companyInfo.secondaryPhoneHref} className="site-nav__drawer-contact">
              <FiPhoneCall aria-hidden="true" />
              {companyInfo.secondaryPhone}
            </a>
          </div>
          <div className="site-nav__drawer-region">
            <FiGlobe aria-hidden="true" />
            <span>{companyInfo.location}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
